<template>
  <BaseModal
    small
    useForm
    :heading="
      isEditing
        ? $t('register.salonized_pay.dashboard.wallets.edit_modal.heading')
        : $t('register.salonized_pay.dashboard.wallets.add_modal.heading')
    "
    @submit="submit"
    @close="$emit('close')"
    testId="sp-wallet-modal"
  >
    <Flex
      direction="column"
      :gap="1.5"
    >
      <BaseInput
        v-model="label"
        :label="$t('register.salonized_pay.dashboard.wallets.add_modal.name_label')"
        :placeholder="$t('register.salonized_pay.dashboard.wallets.add_modal.name_placeholder')"
        :maxLength="MAX_LABEL_LENGTH"
        :error="labelError"
        required
        v-test="'sp-wallet-modal-name'"
      />
      <BaseToggle
        v-if="!isEditing"
        v-model="isTeamMemberWallet"
        :label="$t('register.salonized_pay.dashboard.wallets.add_modal.team_member_label')"
        :description="$t('register.salonized_pay.dashboard.wallets.add_modal.team_member_description', brandNamePay())"
        v-test="'sp-wallet-modal-team-member-toggle'"
      />
      <BaseAlert
        v-if="showsTeamMemberUpsell"
        color="info"
        icon="lock"
        :text="$t('register.salonized_pay.dashboard.wallets.add_modal.team_member_pro_only', brandNamePay())"
        :primaryAction="{
          text: $t('global.upgrade_now'),
          routerLink: { name: 'subscription' }
        }"
        @primaryAction="trackUpgradeClicked"
        v-test="'sp-wallet-modal-team-member-upsell'"
      />
      <BaseDropdown
        v-if="hasTeamMember"
        v-model="employeeId"
        :label="$t('register.salonized_pay.dashboard.wallets.add_modal.team_member_select_label')"
        :placeholder="$t('register.salonized_pay.dashboard.wallets.add_modal.team_member_select_placeholder')"
        :options="employeeOptions"
        :disabled="isEditing"
        :info="isEditing ? $t('register.salonized_pay.dashboard.wallets.edit_modal.team_member_fixed') : undefined"
        :error="employeeError"
        resources
        required
        v-test="'sp-wallet-modal-team-member'"
      />
      <Flex
        direction="column"
        :gap="0.5"
      >
        <BaseInput
          v-model="sharePercentage"
          type="number"
          :label="
            hasTeamMember
              ? $t('register.salonized_pay.dashboard.wallets.add_modal.team_member_share_label', brandNamePay())
              : $t('register.salonized_pay.dashboard.wallets.add_modal.share_label', brandNamePay())
          "
          unitLabel="percentage"
          :decimals="2"
          :minValue="0.01"
          :maxValue="maxSharePercentage"
          :error="shareError"
          required
          v-test="'sp-wallet-modal-share'"
        />
        <BaseText
          color="secondary"
          v-test="'sp-wallet-modal-share-remainder'"
        >
          {{ shareRemainderText }}
        </BaseText>
        <BaseText
          :href="helpCenterArticleUrl(SHARE_ARTICLE_ID)"
          v-test="'sp-wallet-modal-share-help'"
        >
          {{ $t('register.salonized_pay.dashboard.wallets.add_modal.share_help') }}
        </BaseText>
      </Flex>
      <BaseDropdown
        v-model="transferInstrumentId"
        :label="$t('register.salonized_pay.dashboard.wallets.add_modal.pays_out_to_label')"
        :options="payoutOptions"
        :placeholder="
          transferInstrumentRemoved
            ? $t('register.salonized_pay.dashboard.wallets.edit_modal.choose_bank_account')
            : undefined
        "
        :required="isEditing"
        v-test="'sp-wallet-modal-pays-out-to'"
      />
      <BaseAlert
        v-if="transferInstrumentRemoved && transferInstrumentId === null"
        color="error"
        icon="alert"
        :text="$t('register.salonized_pay.dashboard.wallets.edit_modal.bank_account_removed')"
        v-test="'sp-wallet-modal-bank-account-removed'"
      />
      <BaseAlert
        v-if="!otherTransferInstruments.length"
        color="info"
        :text="noOtherBankAccountText"
        :primaryAction="$t('register.salonized_pay.dashboard.business_details.open_adyen')"
        @primaryAction="$emit('generateOnboardingLink')"
        v-test="'sp-wallet-modal-no-bank-account'"
      />
      <BaseAlert
        v-if="generalError"
        color="error"
        :text="generalError"
        v-test="'sp-wallet-modal-error'"
      />
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="$emit('close')"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="loading"
        :disabled="showsTeamMemberUpsell"
        submitForm
        v-test="'sp-wallet-modal-submit'"
      >
        {{ isEditing
            ? $t('register.salonized_pay.dashboard.wallets.edit_modal.submit')
            : $t('register.salonized_pay.dashboard.wallets.add_modal.submit') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandNamePay } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { flash } from '@/helpers/ui';
import { segment } from '@/segment';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { useResourcesStore } from '@/stores/resources';
import {
  type AdyenBalanceAccount,
  type AdyenTransferInstrument,
  type Error as MutationError,
  SubscriptionPlan,
} from '@/types';
import {
  CREATE_ADYEN_BALANCE_ACCOUNT,
  UPDATE_ADYEN_BALANCE_ACCOUNT,
} from '../graphql';
import { BASIS_POINTS_PER_WHOLE, formatShare } from './wallet-share';

const MAX_LABEL_LENGTH = 50;
const SHARE_ARTICLE_ID = 17602940;
// Matches the backend's routing cap in Adyen::AdyenPaymentRule.
const MAX_ROUTED_BASIS_POINTS = 9000;

const props = defineProps<{
  balanceAccounts: AdyenBalanceAccount[];
  // When set, the modal edits this labelled balance account instead of creating one.
  balanceAccount?: AdyenBalanceAccount;
}>();

const emit = defineEmits<{
  close: [];
  generateOnboardingLink: [];
}>();

const { t } = useI18n();
const { company } = useCompanyStore();
const resourcesStore = useResourcesStore();

const { adyenAppState } = useAdyenStore();
const transferInstruments = computed(
  () => adyenAppState.transferInstruments ?? [],
);

const isEditing = computed(() => !!props.balanceAccount);

const proOnlyFeature =
  company.activeSubscription?.plan === SubscriptionPlan.Pro.toLowerCase();

const currentLabel = props.balanceAccount?.label ?? '';
const sweepTransferInstrumentId =
  props.balanceAccount?.adyenSweep?.adyenTransferInstrument?.id ?? null;

// Removed bank accounts are no longer listed, so a wallet still paying out to
// one starts without a selection and asks for a new bank account.
const transferInstrumentRemoved =
  sweepTransferInstrumentId !== null &&
  !transferInstruments.value.some(
    (instrument) => instrument.id === sweepTransferInstrumentId,
  );
const currentTransferInstrumentId = transferInstrumentRemoved
  ? null
  : sweepTransferInstrumentId;

const currentBasisPoints = props.balanceAccount?.paymentRule?.basisPoints ?? 0;

// A wallet's team member is set when it is created and cannot be changed.
const currentEmployeeId =
  props.balanceAccount?.paymentRule?.employee?.id ?? null;

const label = ref(currentLabel);
const isTeamMemberWallet = ref(!!currentEmployeeId);
const employeeId = ref<number | null>(currentEmployeeId);
const sharePercentage = ref(currentBasisPoints / 100);

// When creating, null pays out to wherever the main wallet does, which is the
// backend default. When editing, it is the wallet's current bank account.
const transferInstrumentId = ref<number | null>(currentTransferInstrumentId);

const showsTeamMemberUpsell = computed(
  () => isTeamMemberWallet.value && !proOnlyFeature && !isEditing.value,
);
const hasTeamMember = computed(
  () => isTeamMemberWallet.value && !showsTeamMemberUpsell.value,
);

const trackUpgradeClicked = () => {
  segment.track('register.salonized_pay.wallets.team_member_upgrade.clicked', {
    plan: company.activeSubscription?.plan,
  });
};

const errors = ref<MutationError[]>([]);
const loading = ref(false);

const mainTransferInstrument = computed(
  () =>
    props.balanceAccounts.find((account) => account.isDefault)?.adyenSweep
      ?.adyenTransferInstrument,
);

const otherTransferInstruments = computed(() =>
  transferInstruments.value.filter(
    (instrument) => instrument.id !== mainTransferInstrument.value?.id,
  ),
);

const accountName = (instrument?: AdyenTransferInstrument | null) =>
  [instrument?.bankName, `•••• ${instrument?.accountNumberLast4 ?? ''}`]
    .filter(Boolean)
    .join(' ');

const payoutOptions = computed(() =>
  isEditing.value
    ? // A repoint needs an explicit bank account, so there is no "same as main" option.
      transferInstruments.value.map((instrument) => ({
        value: instrument.id,
        label: accountName(instrument),
      }))
    : [
        {
          value: null,
          label: t(
            'register.salonized_pay.dashboard.wallets.add_modal.same_as_main_wallet',
            { destination: accountName(mainTransferInstrument.value) },
          ),
        },
        ...otherTransferInstruments.value.map((instrument) => ({
          value: instrument.id,
          label: accountName(instrument),
        })),
      ],
);

const noOtherBankAccountText = computed(() =>
  t(
    'register.salonized_pay.dashboard.wallets.add_modal.no_other_bank_account',
    {
      destination: accountName(mainTransferInstrument.value),
    },
  ),
);

const basisPoints = computed(() => Math.round(sharePercentage.value * 100));

const otherPaymentRules = computed(() =>
  props.balanceAccounts
    .filter((account) => account.id !== props.balanceAccount?.id)
    .flatMap((account) => (account.paymentRule ? [account.paymentRule] : [])),
);

// A team member can have only one wallet.
const employeeOptions = computed(() => {
  const takenEmployeeIds = otherPaymentRules.value.flatMap((rule) =>
    rule.employee ? [rule.employee.id] : [],
  );

  return resourcesStore.employees
    .filter((employee) => !takenEmployeeIds.includes(employee.id))
    .map((employee) => ({ value: employee.id, label: employee.name }));
});

// Team member wallets take their share from one team member's payments only,
// so they do not count towards what every card payment hands out.
const otherWalletsBasisPoints = computed(() =>
  otherPaymentRules.value
    .filter((rule) => !rule.employee)
    .reduce((sum, rule) => sum + rule.basisPoints, 0),
);

// A team member's payment is hit by their own wallet and every company-wide
// wallet, so the backend caps that sum.
const maxSharePercentage = computed(() => {
  const largestTeamMemberShare = hasTeamMember.value
    ? 0
    : Math.max(
        0,
        ...otherPaymentRules.value
          .filter((rule) => rule.employee)
          .map((rule) => rule.basisPoints),
      );

  return (
    Math.max(
      0,
      MAX_ROUTED_BASIS_POINTS -
        otherWalletsBasisPoints.value -
        largestTeamMemberShare,
    ) / 100
  );
});

const companyBasisPoints = computed(() =>
  Math.max(
    0,
    BASIS_POINTS_PER_WHOLE - otherWalletsBasisPoints.value - basisPoints.value,
  ),
);

const shareRemainderText = computed(() => {
  const key = hasTeamMember.value
    ? 'team_member_share_remainder'
    : 'share_remainder';

  return otherWalletsBasisPoints.value
    ? t(
        `register.salonized_pay.dashboard.wallets.add_modal.${key}_with_others`,
        {
          ...brandNamePay(),
          company: company.name,
          share: formatShare(companyBasisPoints.value),
          others: formatShare(otherWalletsBasisPoints.value),
        },
      )
    : t(`register.salonized_pay.dashboard.wallets.add_modal.${key}`, {
        ...brandNamePay(),
        company: company.name,
        share: formatShare(companyBasisPoints.value),
      });
});

const FIELD_ATTRIBUTES = ['label', 'basis_points', 'employee_id'];

const labelError = computed(
  () =>
    errors.value.find((error) => error.attribute === 'label')?.message ?? '',
);

const shareError = computed(
  () =>
    errors.value.find((error) => error.attribute === 'basis_points')?.message ??
    '',
);

const employeeError = computed(
  () =>
    errors.value.find((error) => error.attribute === 'employee_id')?.message ??
    '',
);

const generalError = computed(
  () =>
    errors.value.find(
      (error) => !FIELD_ATTRIBUTES.includes(error.attribute ?? ''),
    )?.message ?? '',
);

// Refetch by name: queries default to no-cache, so a { query } refetch would
// only update the cache, which the wallet list never reads.
const refetchOptions = {
  refetchQueries: ['getAdyenWallets'],
  awaitRefetchQueries: true,
};

const { mutate: createBalanceAccount } = useMutation(
  CREATE_ADYEN_BALANCE_ACCOUNT,
  refetchOptions,
);

const { mutate: updateBalanceAccount } = useMutation(
  UPDATE_ADYEN_BALANCE_ACCOUNT,
  refetchOptions,
);

const create = async () => {
  const response = await createBalanceAccount({
    input: {
      label: label.value.trim(),
      transferInstrumentId: transferInstrumentId.value,
      basisPoints: basisPoints.value,
      employeeId: hasTeamMember.value ? employeeId.value : null,
    },
  });
  const result = response?.data?.createAdyenBalanceAccount;

  // When only the payout route fails, the wallet already exists at Adyen and
  // is kept; its card then shows that no payout method is configured.
  if (result?.adyenBalanceAccount) {
    flash(
      t('register.salonized_pay.dashboard.wallets.add_modal.flash_created'),
    );
    emit('close');
    return;
  }

  errors.value = result?.errors ?? [];
};

const update = async (balanceAccount: AdyenBalanceAccount) => {
  const newLabel = label.value.trim();
  const labelChanged = newLabel !== currentLabel;
  const transferInstrumentChanged =
    transferInstrumentId.value !== currentTransferInstrumentId;
  const shareChanged = basisPoints.value !== currentBasisPoints;

  if (!labelChanged && !transferInstrumentChanged && !shareChanged) {
    emit('close');
    return;
  }

  // The backend leaves omitted fields as they are.
  const response = await updateBalanceAccount({
    input: {
      id: balanceAccount.id,
      ...(labelChanged ? { label: newLabel } : {}),
      ...(transferInstrumentChanged
        ? { transferInstrumentId: transferInstrumentId.value }
        : {}),
      ...(shareChanged ? { basisPoints: basisPoints.value } : {}),
    },
  });
  const result = response?.data?.updateAdyenBalanceAccount;

  // A rename can succeed while the repoint fails; the wallet then comes back
  // with errors, so keep the modal open to show them.
  if (result?.adyenBalanceAccount && !result.errors?.length) {
    flash(
      t('register.salonized_pay.dashboard.wallets.edit_modal.flash_updated'),
    );
    emit('close');
    return;
  }

  errors.value = result?.errors ?? [];
};

const submit = async () => {
  if (showsTeamMemberUpsell.value) {
    return;
  }

  loading.value = true;
  errors.value = [];

  try {
    if (props.balanceAccount) {
      await update(props.balanceAccount);
    } else {
      await create();
    }
  } finally {
    loading.value = false;
  }
};
</script>
